/**
 * Pure builders for the freelancer embed snippet — the exact markup users
 * paste on their own site to show the widget served from
 * /embed/freelancer/:id (see server/embed/). Kept free of React so the
 * snippet contract is unit-testable.
 */

const escapeHtml = str =>
  String(str).replace(
    /[&<>"']/g,
    ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch])
  );

// Follows the widget's lang param (not the app UI locale); wording matches
// the server-side widget labels in server/embed/labels.js.
export const ATTRIBUTION_TEXT = {
  fi: 'Tiedot vahvistanut Freedomly',
  en: 'Verified by Freedomly',
};

// Deliberately name-free: the freelancer's name must only ever come from
// server-rendered widget content, never from editable host-page HTML.
// Matches pageTitle in server/embed/labels.js and follows the widget lang.
export const IFRAME_TITLE = {
  fi: 'Freedomly-profiili',
  en: 'Freedomly profile',
};

// Start/end markers so the widget's boundaries are obvious inside the
// embedder's own markup. Follows the widget lang like the other texts.
export const SNIPPET_COMMENTS = {
  fi: { start: 'Freedomly-widget alkaa', end: 'Freedomly-widget päättyy' },
  en: { start: 'Freedomly widget start', end: 'Freedomly widget end' },
};

// The widget's content blocks, in the canonical order the server expects
// (see server/embed/router.js). 'user' = profile header, 'rating' =
// aggregated reviews stat, 'billed' = billed tier stat, 'testimonials' =
// testimonial count stat. Highlighted quotes travel separately in `quotes`.
export const SECTION_KEYS = ['user', 'rating', 'billed', 'testimonials'];

// Fallback dimensions for hosts that strip scripts — normally the resize
// listener hugs the content. Height is a rough estimate per selected block;
// the base covers card padding, the brand logo row and the footer link.
export const snippetDimensions = (sections = SECTION_KEYS, quoteCount = 0) => {
  const hasStats = sections.some(s => s === 'rating' || s === 'billed' || s === 'testimonials');
  const height = 96 + (sections.includes('user') ? 76 : 0) + (hasStats ? 64 : 0) + quoteCount * 112;
  return { width: 340, height: Math.max(140, height) };
};

// sections is always pinned explicitly ('none' when nothing is selected) so
// pasted widgets keep showing exactly what was chosen; quotes are
// type-prefixed ref strings (t.<id> testimonial, r.<id> off-platform review,
// p.<uuid> on-platform review), max two — see server/embed/router.js.
export const buildEmbedPath = (userId, { theme, lang, sections = SECTION_KEYS, quotes = [] }) => {
  const sectionsParam = sections.length > 0 ? sections.join(',') : 'none';
  const quotesParam = quotes.length > 0 ? `&quotes=${quotes.join(',')}` : '';
  return `/embed/freelancer/${userId}?theme=${theme}&lang=${lang}&sections=${sectionsParam}${quotesParam}`;
};

// AggregateRating structured data about the freelancer's services, baked
// with the values at copy time; the snippet's listener refreshes the values
// from the widget's postMessage on render. `\u003c`-escaping keeps the block
// safe to inline even if a name ever contained "</script>".
const jsonLdHtml = (userId, schema) => {
  const payload = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${schema.profileUrl}#rating`,
    name: schema.name,
    url: schema.profileUrl,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(schema.ratingValue),
      reviewCount: schema.reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
  };
  return `<script type="application/ld+json" id="freedomly-rating-${userId}">${JSON.stringify(
    payload
  ).replace(/</g, '\\u003c')}</script>`;
};

// The exact markup users paste on their site.
//
// An iframe src passes no link equity, so the snippet also includes a real
// anchor in the host page's DOM: a visible, branded attribution link to the
// freelancer's profile. It links the clean canonical profile URL (the
// widget's internal footer link carries ?source=embed for tracking) and is
// intentionally easy for embedders to remove.
//
// The trailing <script src> loads the hosted helper
// (server/embed/widget-script.js served at /embed/widget.js): it resizes the
// iframe to hug the widget content and refreshes the optional JSON-LD block
// with live rating values. Hosting it keeps the snippet short and lets us
// update behavior without users re-pasting — which is also why the tag
// carries no Subresource Integrity hash: pinning the content would silently
// break every pasted snippet on our first update, and embedding a vendor
// widget implies trusting the vendor origin (the iframe is ours anyway).
// The iframe height attribute and the baked JSON-LD values are fallbacks
// for hosts that strip scripts or crawlers that don't execute them.
export const buildEmbedSnippet = ({
  rootUrl,
  userId,
  theme,
  lang,
  sections = SECTION_KEYS,
  quotes = [],
  schema = null,
}) => {
  const { width, height } = snippetDimensions(sections, quotes.length);
  const src = `${rootUrl}${buildEmbedPath(userId, { theme, lang, sections, quotes })}`;
  const profileUrl = `${rootUrl}/u/${userId}`;
  const anchorText = ATTRIBUTION_TEXT[lang] || ATTRIBUTION_TEXT.fi;
  const title = IFRAME_TITLE[lang] || IFRAME_TITLE.fi;
  const comments = SNIPPET_COMMENTS[lang] || SNIPPET_COMMENTS.fi;

  return [
    `<!-- ${comments.start} -->`,
    `<div style="width:${width}px;">`,
    `  <iframe src="${src}" width="${width}" height="${height}" style="border:0;display:block;" loading="lazy" title="${escapeHtml(
      title
    )}"></iframe>`,
    `  <a href="${profileUrl}" target="_blank" rel="noopener" style="display:block;margin-top:6px;text-align:center;font:12px/1.4 sans-serif;color:#8f8f8f;text-decoration:none;">${escapeHtml(
      anchorText
    )}</a>`,
    `</div>`,
    ...(schema ? [jsonLdHtml(userId, schema)] : []),
    `<script src="${rootUrl}/embed/widget.js"></script>`,
    `<!-- ${comments.end} -->`,
  ].join('\n');
};
